import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { tap } from 'rxjs';
import { UiAccessService } from '../services/ui-access.service';
import { UserManagementService } from '../services/user-management.service';

/**
 * After any successful IAM mutation (add/edit/delete of user / role / permission / permission-group),
 * reload the ui-access config so control gating reflects the change LIVE — no re-login needed.
 *
 * The reload re-emits accessConfig$, which every [appUiAccess] directive is subscribed to, so
 * mounted controls re-evaluate immediately. GET /ui-access/all is excluded so this never loops.
 */
const IAM_PATH = /\/(roles?|permissions?|permission-groups?|groups?|users?)\b/i;
const MUTATIONS = ['POST', 'PUT', 'PATCH', 'DELETE'];

// Exclude auth, session, and registration endpoints that match /users but are NOT IAM management mutations
const EXCLUDED_PATTERNS = [
  'ui-access',
  'create-new',
  'create-super-admin',
  'is-session-valid',
  'login',
  'token',
  'auth'
];

export const UiAccessRefreshInterceptor: HttpInterceptorFn = (req, next) => {
  const uiAccess = inject(UiAccessService);
  const userMgmt = inject(UserManagementService);

  const urlLower = req.url.toLowerCase();
  const isExcluded = EXCLUDED_PATTERNS.some(pattern => urlLower.includes(pattern));

  const isIamMutation =
    MUTATIONS.includes(req.method) &&
    IAM_PATH.test(req.url) &&
    !isExcluded;

  return next(req).pipe(
    tap(event => {
      if (
        isIamMutation &&
        event instanceof HttpResponse &&
        event.status >= 200 &&
        event.status < 300
      ) {
        // Invalidate cached permission groups so next catalogue view fetches fresh state
        userMgmt.invalidatePermissionGroupsCache();
        // Fire-and-forget forced refresh; loadAccessConfig swallows its own errors.
        uiAccess.loadAccessConfig(true).subscribe();
      }
    })
  );
};
